import React, { useState, useEffect, useRef, useCallback } from 'react';
import TruckVisualizer, { TruckVisualizerHandle } from './components/TruckVisualizer';
import InputForm from './components/InputForm';
import LoadManager from './components/LoadManager';
import ExportModal from './components/ExportModal';
import Logo from './components/Logo';
import { PalletConfig, LoadResult, TRUCK_PRESETS, TruckSpecs, UnitSystem, PlacedPallet } from './types';
import { findNextPosition } from './utils/packingLogic';
import { translations as t } from './utils/translations';
import { toDisplayWeight, toDisplayMeters, toStoredMeters, getTruckDimLabel, getWeightLabel, toDisplayLength } from './utils/units';
import { getEffectiveDimensions } from './utils/snapLogic';
import { Scale, AlertTriangle, Settings, Save, Calculator, Trash2, Box, AlertOctagon, Info, HelpCircle, FileText, FileDown, RotateCw } from 'lucide-react';

const App: React.FC = () => {
  // Global Settings
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric');

  // Data State
  const [currentTruck, setCurrentTruck] = useState<TruckSpecs>(TRUCK_PRESETS[0]);
  
  // Custom Truck Input State
  const [customTruckDims, setCustomTruckDims] = useState({ length: 13.6, width: 2.48, height: 2.7, maxWeight: 24000 });
  const [showCustomTruckInput, setShowCustomTruckInput] = useState(false);

  // Core State
  const [loadResult, setLoadResult] = useState<LoadResult>({
    placedPallets: [],
    totalWeight: 0,
    usedLength: 0,
    percentFull: 0,
    overweight: false
  });
  
  const [isLoadManagerOpen, setIsLoadManagerOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  
  // Alert State
  const [alertState, setAlertState] = useState<{title: string, msg: string, type: 'error' | 'success'} | null>(null);

  // Ref for Visualizer (for screenshots and camera control)
  const visualizerRef = useRef<TruckVisualizerHandle>(null);

  // Recalculate stats whenever placedPallets changes (position or addition)
  useEffect(() => {
    if (loadResult.placedPallets.length === 0) {
       setLoadResult(prev => ({
        ...prev,
        totalWeight: 0,
        usedLength: 0,
        percentFull: 0,
        overweight: false
       }));
       return;
    }

    const totalWeight = loadResult.placedPallets.reduce((acc, p) => acc + p.weight, 0);
    // Find the furthest point used in Z
    const maxZ = Math.max(...loadResult.placedPallets.map(p => p.z + (getEffectiveDimensions(p.width, p.length, p.rotation).length/2)));
    // Convert Z from center-relative to length from front
    // Z ranges from -L/2 to +L/2. 
    // Used length = (maxZ - (-L/2)) = maxZ + L/2
    const usedLen = maxZ + (currentTruck.length / 2);
    
    setLoadResult(prev => ({
        ...prev,
        totalWeight,
        usedLength: Math.max(0, usedLen),
        percentFull: (Math.max(0, usedLen) / currentTruck.length) * 100,
        overweight: totalWeight > currentTruck.maxWeight
    }));
  }, [loadResult.placedPallets, currentTruck]);

  // Auto-dismiss ONLY for success messages
  useEffect(() => {
    if (alertState && alertState.type === 'success') {
      const timer = setTimeout(() => setAlertState(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [alertState]);


  // Helper function to validate pallet against truck dimensions
  const validatePallet = useCallback((pallet: PalletConfig): { valid: boolean; error?: string; title?: string } => {
    const pWidthM = pallet.width / 100;
    const pLengthM = pallet.length / 100;
    const pHeightM = pallet.height / 100;

    // 1. Check Height
    if (pHeightM > currentTruck.height + 0.02) { // 2cm margin
        return { 
            valid: false, 
            title: "DIMENSION ERROR: Too Tall",
            error: `The item "${pallet.name}" is ${toDisplayMeters(pHeightM, unitSystem)} tall. The maximum height of the selected truck is ${toDisplayMeters(currentTruck.height, unitSystem)}.` 
        };
    }

    // 2. Check Fit (allowing rotation)
    const minHorizontalDim = Math.min(pWidthM, pLengthM);
    const maxHorizontalDim = Math.max(pWidthM, pLengthM);
    
    // Check Width
    if (minHorizontalDim > currentTruck.width + 0.02) {
        return { 
            valid: false, 
            title: "DIMENSION ERROR: Too Wide",
            error: `The item "${pallet.name}" is too wide (${toDisplayMeters(minHorizontalDim, unitSystem)}) to fit in the truck width (${toDisplayMeters(currentTruck.width, unitSystem)}), even if rotated.` 
        };
    }

    // Check Length
    if (maxHorizontalDim > currentTruck.length + 0.02) {
         return { 
             valid: false, 
             title: "DIMENSION ERROR: Too Long",
             error: `The item "${pallet.name}" is longer (${toDisplayMeters(maxHorizontalDim, unitSystem)}) than the entire truck length (${toDisplayMeters(currentTruck.length, unitSystem)}).` 
         };
    }
    return { valid: true };
  }, [currentTruck, unitSystem]);

  // Add Single Pallet
  const handleAddPallet = useCallback((newPallet: PalletConfig) => {
    const validation = validatePallet(newPallet);
    if (!validation.valid) {
        setAlertState({
            title: validation.title || "Error",
            msg: validation.error || "Dimension error",
            type: 'error'
        });
        return;
    }

    setLoadResult(prev => {
        // 1. Calculate positions based on current state
        const newPlaced = findNextPosition(newPallet, prev.placedPallets, currentTruck);
        // 2. Append
        return {
            ...prev,
            placedPallets: [...prev.placedPallets, ...newPlaced]
        };
    });
  }, [currentTruck, validatePallet]);

  // Handle Batch Import (AI) - Prevents Stacking
  const handleImportPallets = useCallback((importedPallets: PalletConfig[]) => {
    setLoadResult(prev => {
        let currentPlaced = [...prev.placedPallets];
        let addedCount = 0;
        let failedCount = 0;

        importedPallets.forEach(pallet => {
            const validation = validatePallet(pallet);
            
            if (validation.valid) {
                const newPlaced = findNextPosition(pallet, currentPlaced, currentTruck);
                currentPlaced = [...currentPlaced, ...newPlaced];
                addedCount++;
            } else {
                failedCount++;
            }
        });

        // Set alert after processing state update
        if (failedCount > 0) {
            setAlertState({
                title: "Import Completed with Warnings",
                msg: `Successfully imported ${addedCount} items. ${failedCount} items were skipped because they were too large for this truck.`,
                type: 'error'
            });
        } else {
            setAlertState({
                title: "Import Successful",
                msg: `Successfully added ${addedCount} items to the plan.`,
                type: 'success'
            });
        }

        return {
            ...prev,
            placedPallets: currentPlaced
        };
    });
  }, [currentTruck, validatePallet]);

  const handleRemovePallet = useCallback((id: string) => {
      setLoadResult(prev => ({
          ...prev,
          placedPallets: prev.placedPallets.filter(p => p.id !== id)
      }));
  }, []);

  const handlePalletMove = useCallback((id: string, newPos: { x: number, y: number, z: number, stackedOn: string | null }) => {
    setLoadResult(prev => {
      const updatedPallets = prev.placedPallets.map(p => 
        p.id === id ? { ...p, x: newPos.x, y: newPos.y, z: newPos.z, stackedOn: newPos.stackedOn } : p
      );
      return { ...prev, placedPallets: updatedPallets };
    });
  }, []);

  const handleRotatePallet = useCallback((id: string) => {
    setLoadResult(prev => {
        const pallet = prev.placedPallets.find(p => p.id === id);
        if (!pallet) return prev;

        // Rotate 90 degrees (PI/2)
        const newRotation = pallet.rotation + (Math.PI / 2);
        
        // Calculate new effective dimensions
        const effective = getEffectiveDimensions(pallet.width, pallet.length, newRotation);
        
        // Simple validation: check if rotating puts it out of bounds centered at current pos
        // For a better UX, we could try to push it inside, but for now we just allow rotation
        // as the user can drag it back in.
        // However, let's at least clamp to walls if it goes out.
        
        let newX = pallet.x;
        let newZ = pallet.z;
        
        const maxX = (currentTruck.width / 2) - (effective.width / 2);
        const maxZ = (currentTruck.length / 2) - (effective.length / 2);
        
        // Clamp to ensure it doesn't clip through walls immediately upon rotation
        if (Math.abs(newX) > maxX) newX = Math.sign(newX) * maxX;
        if (Math.abs(newZ) > maxZ) newZ = Math.sign(newZ) * maxZ;

        return {
            ...prev,
            placedPallets: prev.placedPallets.map(p => 
                p.id === id ? { ...p, rotation: newRotation, x: newX, z: newZ } : p
            )
        };
    });
  }, [currentTruck]);

  const handleLoadConfiguration = useCallback((loadedPallets: PlacedPallet[], truckId: string) => {
    const truck = TRUCK_PRESETS.find(t => t.id === truckId) || TRUCK_PRESETS[0];
    setCurrentTruck(truck);
    
    setLoadResult({
        placedPallets: loadedPallets,
        totalWeight: 0, 
        usedLength: 0,
        percentFull: 0,
        overweight: false
    });
  }, []);

  const updateCustomTruck = (key: string, value: string) => {
    const val = parseFloat(value);
    const newDims = { ...customTruckDims, [key]: val };
    setCustomTruckDims(newDims);
    
    if (currentTruck.id === 'custom') {
      setCurrentTruck({
        ...currentTruck,
        length: key === 'maxWeight' ? currentTruck.length : toStoredMeters(newDims.length, unitSystem),
        width: key === 'maxWeight' ? currentTruck.width : toStoredMeters(newDims.width, unitSystem),
        height: key === 'maxWeight' ? currentTruck.height : toStoredMeters(newDims.height, unitSystem),
        maxWeight: key === 'maxWeight' ? newDims.maxWeight : currentTruck.maxWeight
      });
    }
  };

  const applyCustomTruck = () => {
    setCurrentTruck({
        id: 'custom',
        name: t.customTruck,
        region: 'Custom',
        length: toStoredMeters(customTruckDims.length, unitSystem),
        width: toStoredMeters(customTruckDims.width, unitSystem),
        height: toStoredMeters(customTruckDims.height, unitSystem),
        maxWeight: customTruckDims.maxWeight
    });
    setShowCustomTruckInput(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans flex flex-col relative">
      
      {/* ALERT MODAL OVERLAY - Accessible */}
      {alertState && (
        <div 
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="alert-title"
          aria-describedby="alert-desc"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        >
            <div className={`bg-slate-900 border-2 rounded-xl shadow-2xl max-w-md w-full p-6 relative ${alertState.type === 'error' ? 'border-red-500' : 'border-green-500'}`}>
                
                <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-full shrink-0 ${alertState.type === 'error' ? 'bg-red-900/50 text-red-500' : 'bg-green-900/50 text-green-500'}`}>
                        {alertState.type === 'error' ? <AlertOctagon size={32} /> : <Box size={32} />}
                    </div>
                    <div className="flex-1">
                        <h3 id="alert-title" className={`text-lg font-bold mb-1 ${alertState.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                            {alertState.title}
                        </h3>
                        <p id="alert-desc" className="text-slate-300 text-sm leading-relaxed">
                            {alertState.msg}
                        </p>
                    </div>
                </div>

                <div className="mt-6 flex justify-end">
                    <button 
                        onClick={() => setAlertState(null)}
                        className={`px-6 py-2 rounded-lg font-bold text-white transition transform active:scale-95 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 ${alertState.type === 'error' ? 'bg-red-600 hover:bg-red-500 focus:ring-red-500' : 'bg-green-600 hover:bg-green-500 focus:ring-green-500'}`}
                    >
                        OK, I Understand
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 p-4 sticky top-0 z-40 shadow-md">
        <div className="container mx-auto flex flex-col xl:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 w-full xl:w-auto justify-between xl:justify-start">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <Logo className="w-10 h-10 drop-shadow-lg" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white tracking-tight leading-none">{t.appTitle}</h1>
                <p className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">{t.appSubtitle}</p>
              </div>
            </div>
            
            <div className="flex xl:hidden gap-2">
               <button 
                onClick={() => setUnitSystem(u => u === 'metric' ? 'imperial' : 'metric')} 
                className="p-2 bg-slate-800 rounded border border-slate-700 font-bold text-xs w-8 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                aria-label={`Switch to ${unitSystem === 'metric' ? 'Imperial' : 'Metric'} units`}
               >
                  {unitSystem === 'metric' ? 'CM' : 'IN'}
               </button>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-3 w-full xl:w-auto">
            {/* Desktop Units */}
            <div className="hidden xl:flex items-center bg-slate-800 rounded-lg p-1 border border-slate-700 mr-2" role="radiogroup" aria-label="Unit System">
              <button 
                onClick={() => setUnitSystem('metric')}
                className={`px-3 py-1 rounded text-xs font-bold transition focus:ring-2 focus:ring-blue-500 focus:outline-none ${unitSystem === 'metric' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}
                aria-checked={unitSystem === 'metric'}
                role="radio"
              >Metric</button>
              <button 
                onClick={() => setUnitSystem('imperial')}
                className={`px-3 py-1 rounded text-xs font-bold transition focus:ring-2 focus:ring-blue-500 focus:outline-none ${unitSystem === 'imperial' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}
                aria-checked={unitSystem === 'imperial'}
                role="radio"
              >Imperial</button>
            </div>

            <button 
              onClick={() => setIsExportModalOpen(true)}
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 px-3 py-2 rounded-lg flex items-center gap-2 transition text-sm font-medium focus:ring-2 focus:ring-green-500 focus:outline-none"
              aria-label="Export to PDF"
            >
              <FileDown className="w-4 h-4 text-green-400" aria-hidden="true" />
              <span className="hidden sm:inline">Export PDF</span>
            </button>

            <button 
              onClick={() => setIsLoadManagerOpen(true)}
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 px-3 py-2 rounded-lg flex items-center gap-2 transition text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
              aria-label="Open Load Manager"
            >
              <Save className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">{t.saveLoad}</span>
            </button>

            {/* Truck Selector */}
            <div className="flex items-center gap-2 bg-slate-800 p-1.5 rounded-lg border border-slate-700 relative">
               <Settings className="w-4 h-4 text-slate-400 ml-2" aria-hidden="true" />
               <div className="flex flex-col">
                   <label htmlFor="truck-selector" className="sr-only">Select Truck Type</label>
                   <select 
                     id="truck-selector"
                     className="bg-transparent text-sm text-white font-bold focus:outline-none cursor-pointer w-[180px] focus:ring-2 focus:ring-blue-500 rounded"
                     value={currentTruck.id}
                     onChange={(e) => {
                       if (e.target.value === 'custom') {
                         setShowCustomTruckInput(true);
                       } else {
                         const truck = TRUCK_PRESETS.find(t => t.id === e.target.value);
                         if (truck) {
                            setCurrentTruck(truck);
                            setShowCustomTruckInput(false);
                         }
                       }
                     }}
                   >
                     {TRUCK_PRESETS.filter(t => t.id !== 'custom').map(truck => (
                       <option key={truck.id} value={truck.id} className="bg-slate-800">
                         {truck.name}
                       </option>
                     ))}
                     <option value="custom" className="bg-slate-900 text-blue-300 font-bold">+ {t.customTruck}</option>
                   </select>
                   <span className="text-[10px] text-slate-400 pl-1">
                      {toDisplayMeters(currentTruck.length, unitSystem)} x {toDisplayMeters(currentTruck.width, unitSystem)} x {toDisplayMeters(currentTruck.height, unitSystem)}
                   </span>
               </div>
            </div>
          </div>
        </div>
        
        {/* Custom Truck Popup */}
        {showCustomTruckInput && (
           <div className="absolute top-full right-4 mt-2 p-4 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 w-64" role="dialog" aria-label="Custom Truck Configuration">
              <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                 <Calculator size={14} aria-hidden="true" /> {t.customTruck}
              </h3>
              <div className="space-y-2">
                <div>
                   <label htmlFor="custom-length" className="text-xs text-slate-300 block mb-1">{t.length} ({getTruckDimLabel(unitSystem)})</label>
                   <input id="custom-length" type="number" value={customTruckDims.length} onChange={(e) => updateCustomTruck('length', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"/>
                </div>
                <div>
                   <label htmlFor="custom-width" className="text-xs text-slate-300 block mb-1">{t.width} ({getTruckDimLabel(unitSystem)})</label>
                   <input id="custom-width" type="number" value={customTruckDims.width} onChange={(e) => updateCustomTruck('width', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"/>
                </div>
                <div>
                   <label htmlFor="custom-height" className="text-xs text-slate-300 block mb-1">{t.height} ({getTruckDimLabel(unitSystem)})</label>
                   <input id="custom-height" type="number" value={customTruckDims.height} onChange={(e) => updateCustomTruck('height', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"/>
                </div>
                <div>
                   <label htmlFor="custom-weight" className="text-xs text-slate-300 block mb-1">{t.maxWeight} (kg)</label>
                   <input id="custom-weight" type="number" value={customTruckDims.maxWeight} onChange={(e) => updateCustomTruck('maxWeight', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"/>
                </div>
                <button onClick={applyCustomTruck} className="w-full bg-blue-600 text-white text-xs font-bold py-2 rounded mt-2 hover:bg-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                   Apply Configuration
                </button>
              </div>
           </div>
        )}
      </header>

      <main className="container mx-auto p-4 lg:p-6 flex-grow">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Column: Inputs */}
          <div className="lg:col-span-4 flex flex-col gap-6 h-full min-h-0">
            <InputForm 
              onAddPallet={handleAddPallet}
              onImportPallets={handleImportPallets}
              palletCount={loadResult.placedPallets.length}
              unitSystem={unitSystem}
            />
            
            {/* Stats Summary Card */}
            <div className={`p-5 rounded-xl border transition-colors duration-300 ${loadResult.overweight ? 'bg-red-950/30 border-red-500/50' : 'bg-slate-800 border-slate-700'}`}>
              <div className="flex justify-between items-start mb-4">
                 <h3 className="text-lg font-semibold flex items-center gap-2 text-white">
                    <Scale className="w-5 h-5 text-slate-400" aria-hidden="true" /> {t.truckStats}
                 </h3>
              </div>
              
              <div className="space-y-5">
                {/* Weight Bar */}
                <div>
                  <div className="flex justify-between text-xs mb-1.5 font-medium">
                    <span className="text-slate-300 uppercase tracking-wider">{t.totalWeight}</span>
                    <span className={loadResult.overweight ? 'text-red-400 font-bold' : 'text-white font-bold'}>
                      {toDisplayWeight(loadResult.totalWeight, unitSystem).toLocaleString()} / {toDisplayWeight(currentTruck.maxWeight, unitSystem).toLocaleString()} {getWeightLabel(unitSystem)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-900 rounded-full h-3 border border-slate-700 overflow-hidden" role="progressbar" aria-valuenow={loadResult.totalWeight} aria-valuemax={currentTruck.maxWeight}>
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${loadResult.overweight ? 'bg-red-500' : 'bg-gradient-to-r from-green-500 to-emerald-400'}`} 
                      style={{ width: `${Math.min((loadResult.totalWeight / currentTruck.maxWeight) * 100, 100)}%` }}
                    ></div>
                  </div>
                  {loadResult.overweight && (
                    <div className="flex items-center gap-1 text-red-400 text-xs mt-2 font-bold animate-pulse" role="alert">
                      <AlertTriangle size={12} aria-hidden="true" />
                      <span>{t.overweight}</span>
                    </div>
                  )}
                </div>

                {/* Length Bar */}
                <div>
                   <div className="flex justify-between text-xs mb-1.5 font-medium">
                    <span className="text-slate-300 uppercase tracking-wider">{t.linearMeters}</span>
                    <span className="text-white font-bold">
                      {toDisplayMeters(loadResult.usedLength, unitSystem)} / {toDisplayMeters(currentTruck.length, unitSystem)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-900 rounded-full h-3 border border-slate-700 overflow-hidden" role="progressbar" aria-valuenow={loadResult.usedLength} aria-valuemax={currentTruck.length}>
                     <div 
                      className="h-full bg-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(loadResult.percentFull, 100)}%` }}
                    ></div>
                  </div>
                </div>

                 {/* Usage */}
                 <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="bg-slate-900 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-white">{loadResult.placedPallets.length}</div>
                        <div className="text-[10px] text-slate-400 uppercase tracking-wider">Pallets</div>
                    </div>
                    <div className="bg-slate-900 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-400">{Math.round(loadResult.percentFull)}%</div>
                         <div className="text-[10px] text-slate-400 uppercase tracking-wider">Filled</div>
                    </div>
                 </div>
              </div>
            </div>

            {/* Pallet List Preview - Fixed Scrolling & Accessibility */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden flex flex-col flex-grow min-h-0 max-h-[400px]">
                <div className="p-3 bg-slate-800 border-b border-slate-700 shrink-0">
                    <h4 className="text-sm font-bold text-slate-200">Cargo List</h4>
                </div>
                {/* Added pb-12 to ensure last item is visible and not cut off */}
                <div className="overflow-y-auto flex-1 p-2 space-y-2 pb-12">
                    {loadResult.placedPallets.length === 0 ? (
                        <p className="text-center text-slate-500 text-xs py-8">{t.noLoad}</p>
                    ) : (
                        loadResult.placedPallets.map((p, i) => (
                            <div key={p.id} className="bg-slate-900 p-2 rounded flex justify-between items-center group border border-slate-800 hover:border-slate-600 transition">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p.color }}></div>
                                    <div>
                                        <div className="text-xs font-bold text-slate-300">Item #{i+1}</div>
                                        <div className="text-[10px] text-slate-400">
                                            {toDisplayLength(p.width * 100, unitSystem)}x{toDisplayLength(p.length * 100, unitSystem)} • {toDisplayWeight(p.weight, unitSystem)}{getWeightLabel(unitSystem)}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button 
                                        onClick={() => handleRotatePallet(p.id)} 
                                        className="p-2 text-slate-500 hover:text-blue-400 transition rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        aria-label={`Rotate Item #${i+1}`}
                                        title="Rotate 90°"
                                    >
                                        <RotateCw size={16} aria-hidden="true" />
                                    </button>
                                    <button 
                                        onClick={() => handleRemovePallet(p.id)} 
                                        className="p-2 text-slate-500 hover:text-red-400 transition rounded focus:ring-2 focus:ring-red-500 focus:outline-none"
                                        aria-label={`Remove Item #${i+1}`}
                                        title="Delete"
                                    >
                                        <Trash2 size={16} aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
          </div>

          {/* Right Column: Visualization */}
          <div className="lg:col-span-8 flex flex-col gap-4">
            <TruckVisualizer 
              ref={visualizerRef}
              loadData={loadResult.placedPallets}
              truckSpecs={currentTruck}
              onPalletMove={handlePalletMove}
              onRotatePallet={handleRotatePallet}
              onDeletePallet={handleRemovePallet}
            />
          </div>
        </div>
      </main>

      {/* SEO / Education Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 mt-12 py-12 text-slate-400" role="contentinfo">
        <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-12 text-sm leading-relaxed">
           {/* Column 1: How To */}
           <div>
              <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-blue-400" aria-hidden="true" /> How to use TruckLoad 3D
              </h4>
              <ul className="space-y-4 text-slate-300">
                 <li>
                    <strong className="text-white block mb-1">1. Define Your Cargo</strong>
                    Enter dimensions (length, width, height) and weight. Use the <span className="text-purple-400 font-bold">AI Import</span> to paste data directly from Excel or emails. Supports automatic CBM estimation.
                 </li>
                 <li>
                    <strong className="text-white block mb-1">2. Select Vehicle</strong>
                    Choose from EU Standard Tautliner, Mega Trailer, or US Dry Van. You can also define Custom Vehicle dimensions for specialized transport.
                 </li>
                 <li>
                    <strong className="text-white block mb-1">3. Analyze & Optimize</strong>
                    View the 3D stowage plan. Check Loading Meters (LDM) and Axle Weight. Use the tool to ensure compliance.
                 </li>
              </ul>
           </div>

           {/* Column 2: Glossary */}
           <div>
              <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-400" aria-hidden="true" /> Logistics Glossary & FAQ
              </h4>
              <div className="space-y-4 text-slate-300">
                  <div>
                      <strong className="text-white text-xs uppercase tracking-wide">What is LDM (Loading Meter)?</strong>
                      <p className="mt-1 text-xs">LDM stands for Loading Meter. It is the standard unit of measurement for freight in Europe. 1 LDM equals 1 meter of loading space along the length of the truck. <br/>Formula: <code className="bg-slate-800 px-1 rounded text-blue-200">(Length x Width) / 2.4</code></p>
                  </div>
                  <div>
                      <strong className="text-white text-xs uppercase tracking-wide">What is a Tautliner?</strong>
                      <p className="mt-1 text-xs">Also known as a Curtain Sider, it's the most common truck trailer in Europe. It has flexible curtain sides for easy side loading. Standard dimensions: 13.6m x 2.48m x 2.70m.</p>
                  </div>
                  <div>
                      <strong className="text-white text-xs uppercase tracking-wide">Axle Weight Distribution</strong>
                      <p className="mt-1 text-xs">It refers to how the total weight of the cargo is spread across the truck's axles. Improper distribution can lead to heavy fines or accidents.</p>
                  </div>
                  <div>
                      <strong className="text-white text-xs uppercase tracking-wide">Can I calculate CBM to Pallets?</strong>
                      <p className="mt-1 text-xs">Yes. If you only know the total volume (e.g., "15 CBM"), our AI Import tool can estimate how many standard pallets are required to fit that volume.</p>
                  </div>
              </div>
           </div>

           {/* Column 3: SEO / About */}
           <div>
              <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Info className="w-4 h-4 text-orange-400" aria-hidden="true" /> Free Online Truck Loading Software
              </h4>
              <p className="mb-4 text-slate-300">
                  TruckLoad 3D is the most advanced free online tool for logistics professionals, freight forwarders, and truck drivers. Our 3D Load Calculator helps you optimize cargo space, calculate Loading Meters (LDM), and ensure weight distribution compliance (Axle Load) to avoid fines.
              </p>
              <p className="text-slate-300">
                  Unlike simple 2D calculators, our 3D Stowage Simulator allows you to visualize stacking, rotation, and center of gravity in a realistic environment. Perfect for calculating Euro Pallets, Industrial Pallets, and US Standard Pallets.
              </p>
              <div className="mt-6 text-xs text-slate-500 border-t border-slate-800 pt-4">
                 © 2025 TruckLoad 3D. All rights reserved.
              </div>
           </div>
        </div>
      </footer>

      {/* Load Manager Modal */}
      <LoadManager 
        isOpen={isLoadManagerOpen}
        onClose={() => setIsLoadManagerOpen(false)}
        currentPallets={loadResult.placedPallets}
        currentTruck={currentTruck}
        onLoadConfig={handleLoadConfiguration}
      />

      {/* Export PDF Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        visualizerRef={visualizerRef}
        truck={currentTruck}
        pallets={loadResult.placedPallets}
        stats={loadResult}
        unitSystem={unitSystem}
      />

    </div>
  );
};

export default App;